import React from 'react';
import {useParams} from "react-router-dom";
import Sheet from '@mui/joy/Sheet';
import Table from '@mui/joy/Table';
import Typography from '@mui/joy/Typography';
import Stack from '@mui/joy/Stack';
import IconButton from '@mui/joy/IconButton';
import Button from '@mui/joy/Button';
import Box from '@mui/joy/Box';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import RefreshIcon from '@mui/icons-material/Refresh';
import Card from '@mui/joy/Card';
import Divider from '@mui/joy/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import CircularProgress from '@mui/joy/CircularProgress';
import { formatDateTime } from '../../../common/dateTime';
import { NotificationService } from '../../../common/notification';
import { AdminUserSessionIpRow, AdminUserSessionRow } from '../../../models/user';
import { userSessionIpsApi, userSessionsApi } from '../usersApi';

export function UserSessions() {
    const {id} = useParams();
    const PAGE_SIZE = 5;
    const isMobile = useMediaQuery('(max-width:600px)');
    const userId = React.useMemo(() => {
        try { return id ? BigInt(id) : null; } catch { return null; }
    }, [id]);
    const [sessions, setSessions] = React.useState<AdminUserSessionRow[]>([]);
    const [page, setPage] = React.useState(1);
    const [pageCount, setPageCount] = React.useState(1);
    const [isLoading, setIsLoading] = React.useState(false);
    const [fingerprintModalOpen, setFingerprintModalOpen] = React.useState(false);
    const [fingerprintText, setFingerprintText] = React.useState('');
    const [ipsModalOpen, setIpsModalOpen] = React.useState(false);
    const [ipsLoading, setIpsLoading] = React.useState(false);
    const [ipsSessionId, setIpsSessionId] = React.useState<number | null>(null);
    const [allIps, setAllIps] = React.useState<AdminUserSessionIpRow[]>([]);

    React.useEffect(() => {
        let isMounted = true;
        const run = async () => {
            if (!userId || userId <= BigInt(0)) return;
            setIsLoading(true);
            try {
                const res = await userSessionsApi(userId, page, PAGE_SIZE);
                if (!isMounted) return;
                if (res.result) {
                    const payload = res.value!;
                    setPageCount(Math.max(1, Math.ceil((payload.total || payload.count || 0) / PAGE_SIZE)));
                    setSessions(payload.items);
                } else {
                    NotificationService.alertError('Пожалуйста попробуйте позже или обратитесь в службу поддержки');
                }
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };
        run().catch(console.error);
        return () => { isMounted = false; };
    }, [userId, page]);

    const reload = React.useCallback(async () => {
        if (!userId) return;
        setIsLoading(true);
        try {
            const res = await userSessionsApi(userId, page, PAGE_SIZE);
            if (res.result) {
                const payload = res.value!;
                setPageCount(Math.max(1, Math.ceil((payload.total || payload.count || 0) / PAGE_SIZE)));
                setSessions(payload.items);
            } else {
                NotificationService.alertError('Пожалуйста попробуйте позже или обратитесь в службу поддержки');
            }
        } finally {
            setIsLoading(false);
        }
    }, [userId, page]);

    const openFingerprint = React.useCallback((full: string | undefined) => {
        setFingerprintText(full || '');
        setFingerprintModalOpen(true);
    }, []);

    const openAllIps = React.useCallback(async (sessionId: number) => {
        if (!userId) return;
        setIpsLoading(true);
        setIpsSessionId(sessionId);
        setIpsModalOpen(true);
        try {
            const res = await userSessionIpsApi(userId, sessionId);
            if (res.result) {
                setAllIps(res.value!.items || []);
            } else {
                NotificationService.alertError('Пожалуйста попробуйте позже или обратитесь в службу поддержки');
            }
        } finally {
            setIpsLoading(false);
        }
    }, [userId]);

    const goPrev = React.useCallback(() => setPage(p => Math.max(1, p - 1)), []);
    const goNext = React.useCallback(() => setPage(p => Math.min(pageCount, p + 1)), [pageCount]);

    return (
        <React.Fragment>
            <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 1 }}>
                <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                    <Button size="sm" variant="outlined" startDecorator={<RefreshIcon/>} disabled={isLoading}
                            onClick={reload}>Обновить</Button>
                </Stack>
                {!isMobile ? (
                isLoading ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                        <CircularProgress size="sm" />
                    </Stack>
                ) : (
                <Table
                    stickyHeader
                    sx={{
                        tableLayout: 'auto',
                        '& thead th, & tbody td': { borderRight: '1px solid', borderColor: 'divider' },
                        '& thead th:last-of-type, & tbody td:last-of-type': { borderRight: 'none' }
                    }}
                >
                    <thead>
                    <tr>
                        <th rowSpan={2} style={{ width: 32 }} />
                        <th rowSpan={2} style={{ width: 160 }}>Создана</th>
                        <th rowSpan={2} style={{ width: 180 }}>Последняя активность</th>
                        <th rowSpan={2} style={{ width: 200, whiteSpace: 'nowrap' }}>Fingerprint</th>
                        <th colSpan={3} style={{ textAlign: 'center' }}>IP адреса</th>
                    </tr>
                    <tr>
                        <th style={{whiteSpace: 'nowrap', width: '33.33%'}}>IP адрес</th>
                        <th style={{whiteSpace: 'nowrap', width: '33.33%'}}>Создан</th>
                        <th style={{whiteSpace: 'nowrap', width: '33.33%'}}>Последняя активность</th>
                    </tr>
                    </thead>
                    <tbody>
                    {sessions.map((s) => (
                        <tr key={s.id}>
                            <td style={{ width: 32, textAlign: 'center' }}>
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        mx: 'auto',
                                        bgcolor: s.isActive ? 'success.500' : 'danger.500',
                                    }}
                                />
                            </td>
                            <td style={{ whiteSpace: 'nowrap', width: 160 }}>{formatDateTime(new Date(s.created))}</td>
                            <td style={{ whiteSpace: 'nowrap', width: 180 }}>{formatDateTime(new Date(s.lastActivity))}</td>
                            <td style={{ whiteSpace: 'nowrap', width: 200 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography level="body-sm">
                                        {(s.fingerprint || '').slice(0, 10)}{(s.fingerprint || '').length > 10 ? '…' : ''}
                                    </Typography>
                                    {s.fingerprint ? (
                                        <IconButton size="sm" variant="plain" onClick={() => openFingerprint(s.fingerprint)}>
                                            <VisibilityIcon />
                                        </IconButton>
                                    ) : null}
                                </Stack>
                            </td>
                            <td colSpan={3}>
                                <Table
                                    size="sm"
                                    variant="plain"
                                    sx={{
                                        '--TableCell-paddingY': '4px',
                                        '--TableCell-paddingX': '6px',
                                        tableLayout: 'auto',
                                        '& thead th, & tbody td': { borderRight: '1px solid', borderColor: 'divider' },
                                        '& thead th:last-of-type, & tbody td:last-of-type': { borderRight: 'none' }
                                    }}
                                >
                                    <tbody>
                                    {(s.ips || []).map(ip => (
                                        <tr key={`${s.id}-${ip.id}`}>
                                            <td style={{ width: '33.33%' }}>{ip.ip}</td>
                                            <td style={{whiteSpace: 'nowrap', width: '33.33%'}}>{formatDateTime(new Date(ip.start))}</td>
                                            <td style={{whiteSpace: 'nowrap', width: '33.33%'}}>{formatDateTime(new Date(ip.lastActivity))}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>
                                {((s.ips || []).length >= 10) ? (
                                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.5 }}>
                                        <Button size="sm" variant="plain" onClick={() => openAllIps(s.id)}>Показать все</Button>
                                    </Stack>
                                ) : null}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </Table>
                )) : (
                    isLoading ? (
                        <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                            <CircularProgress size="sm" />
                        </Stack>
                    ) : (
                    <Stack spacing={1}>
                        {sessions.map((s) => (
                            <Card key={s.id} variant="outlined" sx={{ p: 1 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Box
                                            sx={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: '50%',
                                                bgcolor: s.isActive ? 'success.500' : 'danger.500',
                                            }}
                                        />
                                        <Typography level="title-sm">Сессия #{s.id}</Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography level="body-sm">{(s.fingerprint || '').slice(0, 10)}{(s.fingerprint || '').length > 10 ? '…' : ''}</Typography>
                                        {s.fingerprint ? (
                                            <IconButton size="sm" variant="plain" onClick={() => openFingerprint(s.fingerprint)}>
                                                <VisibilityIcon />
                                            </IconButton>
                                        ) : null}
                                    </Stack>
                                </Stack>
                                <Divider sx={{ my: 0.5 }} />
                                <Stack spacing={0.5}>
                                    <Typography level="body-sm"><b>Создана:</b> {formatDateTime(new Date(s.created))}</Typography>
                                    <Typography level="body-sm"><b>Последняя активность:</b> {formatDateTime(new Date(s.lastActivity))}</Typography>
                                </Stack>
                                <Divider sx={{ my: 0.5 }} />
                                <Typography level="body-sm" sx={{ mb: 0.5 }}>IP адреса</Typography>
                                <Stack spacing={0.25}>
                                    {(s.ips || []).map(ip => {
                                        const start = new Date(ip.start);
                                        const end = new Date(ip.lastActivity);
                                        const sameYMD = start.getFullYear() === end.getFullYear()
                                            && start.getMonth() === end.getMonth()
                                            && start.getDate() === end.getDate();
                                        const startStr = formatDateTime(start);
                                        const endStr = formatDateTime(end);
                                        const endDisplay = sameYMD ? endStr.split(' ')[1] : endStr; // only time if same day
                                        return (
                                            <Typography key={`${s.id}-${ip.id}`} level="body-sm" sx={{ fontSize: 12 }}>
                                                <b>{ip.ip}</b> ({startStr}-{endDisplay})
                                            </Typography>
                                        );
                                    })}
                                </Stack>
                                {((s.ips || []).length >= 10) ? (
                                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.5 }}>
                                        <Button size="sm" variant="plain" onClick={() => openAllIps(s.id)}>Показать все</Button>
                                    </Stack>
                                ) : null}
                            </Card>
                        ))}
                    </Stack>
                    )
                )}
                <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ mt: 1 }}>
                    <Button size="sm" variant="outlined" startDecorator={<KeyboardArrowLeftIcon/>} disabled={page <= 1 || isLoading}
                            onClick={goPrev}>Назад</Button>
                    <Typography level="body-sm">{page} / {pageCount}</Typography>
                    <Button size="sm" variant="outlined" endDecorator={<KeyboardArrowRightIcon/>}
                            disabled={page >= pageCount || isLoading}
                            onClick={goNext}>Вперёд</Button>
                </Stack>
            </Sheet>
            <Modal open={fingerprintModalOpen} onClose={() => setFingerprintModalOpen(false)}>
                <ModalDialog>
                    <ModalClose />
                    <Typography level="title-md" sx={{ mb: 1 }}>Fingerprint</Typography>
                    <Sheet variant="soft" sx={{ p: 1, maxWidth: 600, maxHeight: 300, overflow: 'auto' }}>
                        <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{fingerprintText}</Typography>
                    </Sheet>
                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                        <Button onClick={() => setFingerprintModalOpen(false)}>Закрыть</Button>
                    </Stack>
                </ModalDialog>
            </Modal>
            <Modal open={ipsModalOpen} onClose={() => setIpsModalOpen(false)}>
                <ModalDialog>
                    <ModalClose />
                    <Typography level="title-md" sx={{ mb: 1 }}>Все IP адреса {ipsSessionId ? `(сессия #${ipsSessionId})` : ''}</Typography>
                    <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 0, maxWidth: 800, maxHeight: 500, overflow: 'auto' }}>
                        {ipsLoading ? (
                            <Stack alignItems="center" justifyContent="center" sx={{ py: 3 }}>
                                <CircularProgress size="sm" />
                            </Stack>
                        ) : (
                        <Table
                            stickyHeader
                            size="sm"
                            sx={{
                                tableLayout: 'auto',
                                '& thead th, & tbody td': { borderRight: '1px solid', borderColor: 'divider' },
                                '& thead th:last-of-type, & tbody td:last-of-type': { borderRight: 'none' }
                            }}
                        >
                            <thead>
                            <tr>
                                <th style={{ width: '33.33%' }}>IP адрес</th>
                                <th style={{ width: '33.33%' }}>Дата и время начала</th>
                                <th style={{ width: '33.33%' }}>Последняя активность</th>
                            </tr>
                            </thead>
                            <tbody>
                            {allIps.map(ip => (
                                <tr key={ip.id}>
                                    <td style={{ width: '33.33%' }}>{ip.ip}</td>
                                    <td style={{whiteSpace: 'nowrap', width: '33.33%'}}>{formatDateTime(new Date(ip.start))}</td>
                                    <td style={{whiteSpace: 'nowrap', width: '33.33%'}}>{formatDateTime(new Date(ip.lastActivity))}</td>
                                </tr>
                            ))}
                            {(!ipsLoading && allIps.length === 0) ? (
                                <tr>
                                    <td colSpan={3}>
                                        <Typography level="body-sm" sx={{ p: 1 }}>Нет данных</Typography>
                                    </td>
                                </tr>
                            ) : null}
                            </tbody>
                        </Table>
                        )}
                    </Sheet>
                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                        <Button onClick={() => setIpsModalOpen(false)}>Закрыть</Button>
                    </Stack>
                </ModalDialog>
            </Modal>
        </React.Fragment>
    );
}


